<template>
  <div class="flex flex-col" :style="{ height: 'calc(100vh - 210px)' }" data-private>
    <!-- Header + summary -->
    <div class="flex items-center justify-between px-4 pt-4 pb-2">
      <div class="flex items-center gap-3">
        <h2 class="text-lg font-semibold text-gray-800">eCQM Clinical Facts</h2>
        <span v-if="summary.LAST_LOAD" class="text-xs text-gray-400">
          Loaded {{ summary.LAST_LOAD }}
        </span>
      </div>
      <div class="flex items-center gap-4">
        <div v-if="summary.TOTAL_FACTS" class="flex items-center gap-4 text-xs text-gray-500">
          <span><span class="font-semibold text-gray-700">{{ fmt(summary.TOTAL_FACTS) }}</span> facts</span>
          <span><span class="font-semibold text-gray-700">{{ fmt(summary.DOCUMENTS) }}</span> documents</span>
          <span><span class="font-semibold text-gray-700">{{ fmt(summary.PRACTICES) }}</span> practices</span>
        </div>
        <ExportMenu
          v-if="subTab === 'facts'"
          :disabled="exportLoading"
          :exports="[
            { type: 'excel', action: exportExcel, loading: exportLoading, menuLabel: 'Export to Excel' },
            { type: 'csv', action: exportCsv, loading: exportLoading, menuLabel: 'Export to CSV' }
          ]"
        />
      </div>
    </div>

    <!-- Sub-tabs -->
    <div class="px-4 flex gap-1 border-b border-gray-200">
      <button
        v-for="t in subTabs" :key="t.key"
        @click="subTab = t.key"
        class="text-xs px-3 py-2 -mb-px border-b-2 transition-colors"
        :class="subTab === t.key
          ? 'border-primary-500 text-gray-900 font-medium'
          : 'border-transparent text-gray-500 hover:text-gray-700'"
      >{{ t.label }}</button>
    </div>

    <!-- ============================ CLINICAL FACTS ============================ -->
    <template v-if="subTab === 'facts'">
    <!-- Filters -->
    <div class="px-4 pt-2 pb-2 flex flex-wrap items-center gap-3">
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-gray-500 font-medium">Practice</span>
        <select v-model="filters.practice" @change="refreshGrid"
                class="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white min-w-[10rem]">
          <option :value="''">All</option>
          <option v-for="p in options.practices" :key="p" :value="p">{{ p }}</option>
        </select>
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-gray-500 font-medium">Code System</span>
        <select v-model="filters.codeSystem" @change="refreshGrid"
                class="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white">
          <option :value="''">All</option>
          <option v-for="c in options.codeSystems" :key="c" :value="c">{{ c }}</option>
        </select>
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-gray-500 font-medium">Type</span>
        <select v-model="filters.elementType" @change="refreshGrid"
                class="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white">
          <option :value="''">All</option>
          <option v-for="e in options.elementTypes" :key="e" :value="e">{{ e }}</option>
        </select>
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-gray-500 font-medium">Mood</span>
        <select v-model="filters.moodCode" @change="refreshGrid"
                class="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white">
          <option :value="''">All</option>
          <option v-for="m in options.moodCodes" :key="m" :value="m">{{ moodLabel(m) }}</option>
        </select>
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-gray-500 font-medium">Description</span>
        <input
          v-model="filters.description"
          @keyup.enter="refreshGrid"
          @change="refreshGrid"
          type="text"
          placeholder="Filter description (e.g. blood pressure, depression, A1c)…"
          class="text-xs border border-gray-200 rounded px-2.5 py-1.5 bg-white min-w-[15rem]"
        />
      </div>
      <label class="flex items-center gap-1.5 text-xs text-gray-500 font-medium cursor-pointer">
        <input type="checkbox" v-model="filters.valuesOnly" @change="refreshGrid" />
        Results with values only
      </label>
      <input
        v-model="filters.search"
        @keyup.enter="refreshGrid"
        type="text"
        placeholder="Search patient name or code…"
        class="text-xs border border-gray-200 rounded px-2.5 py-1.5 bg-white flex-1 min-w-[12rem]"
      />
      <button @click="resetFilters"
              class="text-xs text-gray-400 hover:text-gray-600 cursor-pointer">Reset</button>
    </div>

    <!-- Grid -->
    <div class="flex-1 px-4 pb-4 min-h-0">
      <AgGridVue
        class="w-full h-full ecqm-facts-grid"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :gridOptions="gridOptions"
        :theme="gridTheme"
        :rowModelType="'serverSide'"
        @grid-ready="onGridReady"
      />
    </div>
    </template>

    <!-- ============================== RAW QRDA =============================== -->
    <template v-else-if="subTab === 'raw'">
    <div class="px-4 pt-2 pb-2 flex flex-wrap items-center gap-3">
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-gray-500 font-medium">Practice</span>
        <select v-model="rawPractice" @change="loadDocuments"
                class="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white min-w-[14rem]">
          <option :value="''" disabled>Select a practice…</option>
          <option v-for="p in options.practices" :key="p" :value="p">{{ p }}</option>
        </select>
      </div>
      <input
        v-model="rawSearch"
        @keyup.enter="loadDocuments"
        type="text"
        placeholder="Search patient name…"
        class="text-xs border border-gray-200 rounded px-2.5 py-1.5 bg-white flex-1 min-w-[12rem]"
      />
      <span v-if="documents.length" class="text-xs text-gray-400">
        {{ documents.length }} document{{ documents.length === 1 ? '' : 's' }}
      </span>
    </div>

    <div class="flex-1 px-4 pb-4 min-h-0 flex gap-3">
      <!-- document list -->
      <div class="w-1/3 h-full overflow-auto border border-gray-200 rounded bg-white min-h-0">
        <div v-if="!rawPractice" class="p-4 text-xs text-gray-500">Select a practice to browse its QRDA documents.</div>
        <div v-else-if="docsLoading" class="p-4 text-xs text-gray-500">Loading documents…</div>
        <div v-else-if="!documents.length" class="p-4 text-xs text-gray-500">No documents.</div>
        <button
          v-for="d in documents" :key="d.SOURCE_FILE + '|' + d.INNER_FILE"
          @click="openDoc(d)"
          class="w-full text-left px-3 py-2 border-b border-gray-100 hover:bg-gray-50 text-xs"
          :class="isSelected(d) ? 'bg-primary-100' : ''"
        >
          <div class="font-medium text-gray-900">{{ d.PATIENT_LAST_NAME }}, {{ d.PATIENT_FIRST_NAME }}</div>
          <div class="text-gray-500">
            DOB {{ fmtQrdaDate(d.PATIENT_BIRTH_DT) }}
            <span v-if="d.MEASURE_IDS"> · {{ d.MEASURE_IDS }}</span>
          </div>
        </button>
      </div>

      <!-- right column: raw xml (top) + AI Q&A (bottom) -->
      <div class="flex-1 h-full flex flex-col gap-3 min-h-0">
        <!-- raw xml -->
        <div class="flex-1 flex flex-col border border-gray-200 rounded bg-white min-h-0">
          <div class="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-primary-100">
            <div class="text-xs">
              <div class="font-semibold text-gray-900">Source QRDA document</div>
              <div v-if="selectedDoc" class="text-gray-600">
                {{ selectedDoc.PATIENT_LAST_NAME }}, {{ selectedDoc.PATIENT_FIRST_NAME }} · {{ selectedDoc.PRACTICE }}
              </div>
            </div>
            <button v-if="selectedDoc" @click="copyXml" class="text-xs text-gray-600 hover:text-gray-900">
              {{ copied ? 'Copied' : 'Copy XML' }}
            </button>
          </div>
          <div class="flex-1 overflow-auto min-h-0">
            <div v-if="docLoading" class="p-4 text-xs text-gray-500">Loading source XML…</div>
            <div v-else-if="!selectedDoc" class="p-4 text-xs text-gray-500">Select a document to view its raw QRDA XML.</div>
            <pre v-else class="p-3 text-[11px] leading-snug text-gray-800 whitespace-pre-wrap break-all font-mono">{{ prettyXml }}</pre>
          </div>
        </div>

        <!-- AI Q&A over the XML (GPT-5.5, Responses API) -->
        <div class="h-2/5 flex flex-col border border-gray-200 rounded bg-white min-h-0">
          <div class="px-3 py-2 border-b border-gray-200 bg-primary-100 flex items-center gap-2">
            <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" class="text-gray-600 text-xs" />
            <span class="text-xs font-semibold text-gray-900">Ask about this document</span>
          </div>
          <div class="flex-1 overflow-auto min-h-0 p-3">
            <div v-if="askError" class="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{{ askError }}</div>
            <div v-else-if="asking" class="text-xs text-gray-500">Thinking…</div>
            <div v-else-if="answer" class="text-xs text-gray-800 whitespace-pre-wrap leading-relaxed">{{ answer }}</div>
            <div v-else class="text-xs text-gray-400">
              Ask a question about this patient's QRDA — e.g. "Is this patient controlled for blood pressure and what's the evidence?"
            </div>
          </div>
          <div class="border-t border-gray-200 p-2 flex items-center gap-2">
            <input
              v-model="question"
              @keyup.enter="askQuestion"
              :disabled="!selectedDoc || asking"
              type="text"
              placeholder="Ask a question about this document…"
              class="flex-1 text-xs border border-gray-200 rounded px-2.5 py-1.5 bg-white disabled:bg-gray-50"
            />
            <button
              @click="askQuestion"
              :disabled="!selectedDoc || asking || !question.trim()"
              class="btn-primary-sm disabled:opacity-50"
            >{{ asking ? '…' : 'Ask' }}</button>
          </div>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3';
import { themeQuartz } from 'ag-grid-community';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx';

export default {
  name: 'Quality_ECQMFacts',
  components: { AgGridVue, ExportMenu },
  data() {
    return {
      loading: false,
      exportLoading: false,
      gridApi: null,
      summary: {},
      subTab: 'facts',
      subTabs: [
        { key: 'facts', label: 'Clinical Facts' },
        { key: 'raw', label: 'Raw QRDA' },
      ],
      // Raw QRDA browser state
      rawPractice: '',
      rawSearch: '',
      documents: [],
      docsLoading: false,
      selectedDoc: null,
      docLoading: false,
      copied: false,
      // AI Q&A state
      question: '',
      answer: '',
      asking: false,
      askError: '',
      options: { practices: [], codeSystems: [], elementTypes: [], moodCodes: [] },
      filters: {
        practice: '',
        codeSystem: '',
        elementType: '',
        moodCode: '',
        valuesOnly: false,
        description: '',
        search: '',
      },
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMovable: false,
      },
      columnDefs: [
        { headerName: 'Practice', field: 'PRACTICE', minWidth: 180 },
        {
          headerName: 'Patient',
          minWidth: 150,
          valueGetter: p => {
            if (!p.data) return '';
            const l = p.data.PATIENT_LAST_NAME || '';
            const f = p.data.PATIENT_FIRST_NAME || '';
            return [l, f].filter(Boolean).join(', ');
          },
        },
        {
          headerName: 'DOB',
          field: 'PATIENT_BIRTH_DT',
          minWidth: 100,
          valueFormatter: p => this.fmtQrdaDate(p.value),
        },
        { headerName: 'Sex', field: 'PATIENT_GENDER_CD', minWidth: 60 },
        { headerName: 'Code System', field: 'CODE_SYSTEM', minWidth: 110 },
        { headerName: 'Code', field: 'CODE', minWidth: 90 },
        { headerName: 'Description', field: 'CODE_DISPLAY', minWidth: 220, flex: 1 },
        {
          headerName: 'Value',
          minWidth: 140,
          valueGetter: p => {
            if (!p.data) return '';
            if (p.data.VALUE != null && p.data.VALUE !== '') {
              return `${p.data.VALUE}${p.data.VALUE_UNIT ? ' ' + p.data.VALUE_UNIT : ''}`;
            }
            if (p.data.VALUE_CODE) {
              const SNOMED_FINDINGS = {
                '428171000124102': 'Screening negative',
                '428181000124104': 'Screening positive',
                '720834000': 'Medical reason / not indicated',
                '183964008': 'Treatment not indicated',
              };
              return SNOMED_FINDINGS[p.data.VALUE_CODE] || p.data.VALUE_CODE;
            }
            return '';
          },
        },
        {
          headerName: 'Date',
          field: 'EFFECTIVE_DT',
          minWidth: 100,
          sort: 'desc',
          valueFormatter: p => this.fmtQrdaDate(p.value),
        },
        {
          headerName: 'Kind',
          field: 'MOOD_CODE',
          minWidth: 80,
          valueFormatter: p => this.moodLabel(p.value),
        },
      ],
      gridOptions: {
        rowModelType: 'serverSide',
        animateRows: true,
        enableCellTextSelection: true,
        ensureDomOrder: true,
        cacheBlockSize: 100,
        autoSizeStrategy: { type: 'fitGridWidth' },
      },
      gridTheme: themeQuartz.withParams({
        browserColorScheme: 'light',
        backgroundColor: '#ffffff',
        foregroundColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderRadius: 0,
        fontFamily: 'inherit',
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 600,
        rowHeight: 30,
        headerHeight: 32,
      }),
    };
  },
  async mounted() {
    await this.loadFilterOptions();
  },
  computed: {
    prettyXml() {
      const raw = this.selectedDoc && this.selectedDoc.RAW_XML;
      if (!raw) return '';
      return this.formatXml(raw);
    },
  },
  methods: {
    fmt(n) {
      return n == null ? '' : Number(n).toLocaleString();
    },
    // QRDA dates arrive as YYYYMMDD[hhmmss...] strings
    fmtQrdaDate(v) {
      if (!v) return '';
      const s = String(v);
      if (s.length >= 8 && /^\d{8}/.test(s)) {
        return `${s.slice(4, 6)}/${s.slice(6, 8)}/${s.slice(0, 4)}`;
      }
      return s;
    },
    moodLabel(m) {
      const map = { EVN: 'Result', RQO: 'Order', INT: 'Intent', PRMS: 'Promise' };
      return map[m] || m || '';
    },
    async loadFilterOptions() {
      try {
        const { data } = await this.$axios.get('/api/qrda-facts/filter-options/');
        this.options.practices = data.practices || [];
        this.options.codeSystems = data.codeSystems || [];
        this.options.elementTypes = data.elementTypes || [];
        this.options.moodCodes = data.moodCodes || [];
        this.summary = data.summary || {};
      } catch (err) {
        console.error('Error loading QRDA fact filters:', err);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      const datasource = {
        getRows: async (req) => {
          try {
            this.loading = true;
            const sortModel = req.request.sortModel && req.request.sortModel[0];
            const { data } = await this.$axios.post('/api/qrda-facts/facts-list/', {
              startRow: req.request.startRow,
              endRow: req.request.endRow,
              practice: this.filters.practice || undefined,
              code_system: this.filters.codeSystem || undefined,
              element_type: this.filters.elementType || undefined,
              mood_code: this.filters.moodCode || undefined,
              values_only: this.filters.valuesOnly || undefined,
              description: this.filters.description || undefined,
              search: this.filters.search || undefined,
              sortColumn: sortModel ? sortModel.colId : undefined,
              sortDir: sortModel ? sortModel.sort : undefined,
            });
            req.success({ rowData: data.rows || [], rowCount: data.totalRows || 0 });
          } catch (err) {
            console.error('Error fetching QRDA facts:', err);
            req.fail();
          } finally {
            this.loading = false;
          }
        },
      };
      this.gridApi.setGridOption('serverSideDatasource', datasource);
    },
    refreshGrid() {
      if (this.gridApi) this.gridApi.refreshServerSide({ purge: true });
    },
    async loadDocuments() {
      if (!this.rawPractice) return;
      this.docsLoading = true;
      this.documents = [];
      this.selectedDoc = null;
      try {
        const { data } = await this.$axios.post('/api/qrda-facts/documents/', {
          practice: this.rawPractice,
          search: this.rawSearch || undefined,
        });
        this.documents = data.rows || [];
      } catch (err) {
        console.error('Error loading QRDA documents:', err);
      } finally {
        this.docsLoading = false;
      }
    },
    isSelected(d) {
      return this.selectedDoc
        && this.selectedDoc.SOURCE_FILE === d.SOURCE_FILE
        && this.selectedDoc.INNER_FILE === d.INNER_FILE;
    },
    async openDoc(d) {
      this.copied = false;
      this.answer = '';
      this.askError = '';
      this.docLoading = true;
      this.selectedDoc = { ...d, RAW_XML: null };
      try {
        const { data } = await this.$axios.post('/api/qrda-facts/document/', {
          source_file: d.SOURCE_FILE,
          inner_file: d.INNER_FILE,
        });
        this.selectedDoc = data;
      } catch (err) {
        console.error('Error loading source QRDA:', err);
        this.selectedDoc = { ...d, RAW_XML: 'Could not load source document.' };
      } finally {
        this.docLoading = false;
      }
    },
    async copyXml() {
      try {
        await navigator.clipboard.writeText(this.selectedDoc.RAW_XML || '');
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 1500);
      } catch (e) { /* clipboard unavailable */ }
    },
    async askQuestion() {
      if (!this.selectedDoc || !this.question.trim() || this.asking) return;
      this.asking = true;
      this.answer = '';
      this.askError = '';
      try {
        const { data } = await this.$axios.post('/api/qrda-facts/ask/', {
          source_file: this.selectedDoc.SOURCE_FILE,
          inner_file: this.selectedDoc.INNER_FILE,
          question: this.question.trim(),
        });
        if (data.success) {
          this.answer = data.answer;
        } else {
          this.askError = data.answer || 'The model could not answer.';
        }
      } catch (err) {
        console.error('Error asking about QRDA:', err);
        this.askError = 'Request failed. Please try again.';
      } finally {
        this.asking = false;
      }
    },
    // lightweight XML pretty-printer (indent on tag boundaries)
    formatXml(xml) {
      const reg = /(>)(<)(\/*)/g;
      let formatted = '';
      let pad = 0;
      xml.replace(reg, '$1\n$2$3').split('\n').forEach((node) => {
        let indent = 0;
        if (node.match(/^<\/\w/)) pad = Math.max(pad - 1, 0);
        else if (node.match(/^<\w[^>]*[^/]>.*$/)) indent = 1;
        formatted += '  '.repeat(pad) + node + '\n';
        pad += indent;
      });
      return formatted.trim();
    },
    resetFilters() {
      this.filters = {
        practice: '', codeSystem: '', elementType: '', moodCode: '',
        valuesOnly: false, description: '', search: '',
      };
      this.refreshGrid();
    },
    async fetchAllFilteredFacts(maxRows = 10000) {
      const { data } = await this.$axios.post('/api/qrda-facts/facts-list/', {
        startRow: 0,
        endRow: maxRows,
        practice: this.filters.practice || undefined,
        code_system: this.filters.codeSystem || undefined,
        element_type: this.filters.elementType || undefined,
        mood_code: this.filters.moodCode || undefined,
        values_only: this.filters.valuesOnly || undefined,
        description: this.filters.description || undefined,
        search: this.filters.search || undefined,
      });
      const rows = data.rows || [];
        const fmtValue = (r) => {
          if (r.VALUE != null && r.VALUE !== '') {
            return `${r.VALUE}${r.VALUE_UNIT ? ' ' + r.VALUE_UNIT : ''}`;
          }
          if (r.VALUE_CODE) {
            const SNOMED_FINDINGS = {
              '428171000124102': 'Screening negative',
              '428181000124104': 'Screening positive',
              '720834000': 'Medical reason / not indicated',
              '183964008': 'Treatment not indicated',
            };
            return SNOMED_FINDINGS[r.VALUE_CODE] || r.VALUE_CODE;
          }
          return '';
        };
        return rows.map((r) => ({
          'Practice': r.PRACTICE || '',
          'Patient Name': [r.PATIENT_LAST_NAME, r.PATIENT_FIRST_NAME].filter(Boolean).join(', '),
          'Birth Date': this.fmtQrdaDate(r.PATIENT_BIRTH_DT),
          'Sex': r.PATIENT_GENDER_CD || '',
          'Code System': r.CODE_SYSTEM || '',
          'Code': r.CODE || '',
          'Description': r.CODE_DISPLAY || '',
          'Value': fmtValue(r),
          'Date': this.fmtQrdaDate(r.EFFECTIVE_DT),
          'Kind': this.moodLabel(r.MOOD_CODE),
        }));
    },
    async exportExcel() {
      this.exportLoading = true;
      try {
        const rows = await this.fetchAllFilteredFacts();
        if (!rows.length) {
          alert('No clinical facts found with current filters.');
          return;
        }
        await exportToExcel(rows, `ecqm_clinical_facts_${new Date().toISOString().split('T')[0]}.xlsx`, {
          sheetName: 'Clinical Facts',
          columnWidths: {
            'Practice': 200,
            'Patient Name': 180,
            'Birth Date': 100,
            'Sex': 60,
            'Code System': 120,
            'Code': 90,
            'Description': 280,
            'Value': 110,
            'Date': 100,
            'Kind': 80,
          },
        });
      } catch (err) {
        console.error('Error exporting clinical facts:', err);
      } finally {
        this.exportLoading = false;
      }
    },
    async exportCsv() {
      this.exportLoading = true;
      try {
        const rows = await this.fetchAllFilteredFacts();
        if (!rows.length) {
          alert('No clinical facts found with current filters.');
          return;
        }
        const headers = Object.keys(rows[0]);
        const csvEscape = (val) => {
          if (val == null) return '';
          const str = String(val);
          return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"` : str;
        };
        const csvLines = [
          headers.map(csvEscape).join(','),
          ...rows.map(r => headers.map(h => csvEscape(r[h])).join(',')),
        ];
        const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ecqm_clinical_facts_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error exporting clinical facts to CSV:', err);
      } finally {
        this.exportLoading = false;
      }
    },
  },
};
</script>
